import { Stack } from '@mui/material';
import { FC, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowRightIcon from '@/assets/svg/ui-elements/arrow-right-square-gray.svg';
import SmsIcon from '@/assets/svg/ui-elements/sms-filled-white.svg';
import { IconButton, Tooltip } from '@/design-system/components';
import { getCommunicationContactPath } from '@/pages/communications-v2/utils/get-communication-contact-path';
import { getSetupFundReviewOrEditPath } from '@/pages/setup-fund/utils/fund-review-routing';
import { DeniedReasonTrigger, Permission, useCan } from '@/shared/modules/permissions';

import { FundsTabTableRow } from '../data';

export const FUND_REVIEW_BLOCKED_TOOLTIP = 'You do not have permission to review this fund';

type Props = Pick<FundsTabTableRow, 'id' | 'name' | 'status' | 'entityId'>;

export const FundsTabTableActions: FC<Props> = ({ id, status, entityId }) => {
  const canReviewCrossEntity = useCan(Permission.GlobalAdminCrossEntityAccess);
  const canReviewFund = useCan(Permission.FundReview);
  const navigate = useNavigate();

  const communicationPath = getCommunicationContactPath({
    recipientType: 'EntityDesk',
    recipientId: entityId,
  });

  const onReview = (e: MouseEvent) => {
    e.stopPropagation();
    navigate(
      getSetupFundReviewOrEditPath({
        id,
        canReviewCrossEntity,
        fundApprovalStatus: status,
        draftStatus: null,
      }),
    );
  };

  const onContact = (e: MouseEvent) => {
    e.stopPropagation();
    navigate(communicationPath);
  };

  return (
    <Stack gap="8px">
      <Tooltip title={canReviewFund ? '' : FUND_REVIEW_BLOCKED_TOOLTIP}>
        <DeniedReasonTrigger denied={!canReviewFund}>
          <IconButton
            aria-label="Review fund"
            color="tertiary"
            variant="outline"
            size="xs"
            disabled={!canReviewFund}
            onClick={onReview}
          >
            <ArrowRightIcon />
          </IconButton>
        </DeniedReasonTrigger>
      </Tooltip>
      <IconButton
        aria-label="Contact fund entity"
        color="tertiary"
        variant="outline"
        size="xs"
        onClick={onContact}
      >
        <SmsIcon />
      </IconButton>
    </Stack>
  );
};
